import { LocalDate } from "@js-joda/core";
import { ListRow } from "components/designV2/listRow/listRow";
import { MetricCell } from "components/designV2/metricCell/metricCell";
import { PeriodNavigator } from "components/designV2/periodNavigator/periodNavigator";
import { Text } from "components/designV2/text/text";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { ColorV2 } from "config/colors";
import { usePDFViewer } from "hooks/usePDFViewer";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "services/translation/i18n";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { ActionDropdownButton } from "../general/actionDropdownButton/actionDropdownButton.component";
import { InfoModal } from "../general/infoModal/infoModal.component";
import {
  ExportGrid,
  ExportRowContent,
  ExportsHeader,
  ExportsPageContainer,
} from "./salaryExports.styles";

type MonthOption = { value: number; label: string };

export const SalaryExportsOverview = (): JSX.Element => {
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(LocalDate.now());
  const [{ company }] = useCompanyStore();
  const { prepareExport } = usePDFViewer();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showInfo, setShowInfo] = useState(false);

  const allowNext = useMemo(
    () => currentDate.plusYears(1).year() <= LocalDate.now().year(),
    [currentDate],
  );

  const months: MonthOption[] = useMemo(
    () => [
      { value: 1, label: t("General.January") },
      { value: 2, label: t("General.February") },
      { value: 3, label: t("General.March") },
      { value: 4, label: t("General.April") },
      { value: 5, label: t("General.May") },
      { value: 6, label: t("General.June") },
      { value: 7, label: t("General.July") },
      { value: 8, label: t("General.August") },
      { value: 9, label: t("General.September") },
      { value: 10, label: t("General.October") },
      { value: 11, label: t("General.November") },
      { value: 12, label: t("General.December") },
    ],
    [t, i18n.language],
  );

  const handleExport = async (
    month: number,
    type: "SIE" | "Hogia",
  ): Promise<void> => {
    setErrorMessage("");
    try {
      setLoading(true);

      const companyId = company?.id;
      const year = currentDate.year();
      if (!companyId) {
        setLoading(false);
        return;
      }

      await prepareExport(month, type, year);

      setShowInfo(true);
    } catch (err: unknown) {
      const message =
        err &&
        typeof err === "object" &&
        "errorMessage" in err &&
        typeof err.errorMessage === "string"
          ? err.errorMessage
          : "NotAvailable";
      setErrorMessage(message);
      setShowInfo(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ExportsPageContainer>
      <ExportsHeader>
        <TooltipHover text={t("Salary.SalaryExportsInfo")} arrow="up">
          <Text typography="s1" color={ColorV2.Blue1} align="right">
            {t("Salary.WhatIs", {
              name: t("Salary.SalaryExports").toLowerCase(),
            })}
          </Text>
        </TooltipHover>
      </ExportsHeader>

      <PeriodNavigator
        currentDate={currentDate}
        mode="year"
        onChange={setCurrentDate}
        allowNext={allowNext}
      />

      <ExportGrid>
        {months.map((month) => (
          <ListRow key={month.value}>
            <ExportRowContent>
              <MetricCell
                label={t("Salary.ExportPeriod")}
                value={month.label}
              />
              <ActionDropdownButton
                disabled={loading}
                action
                size="medium"
                options={[
                  {
                    id: "hogia",
                    label: "Hogia",
                    onClick: () => handleExport(month.value, "Hogia"),
                  },
                  {
                    id: "SIE",
                    label: "SIE",
                    onClick: () => handleExport(month.value, "SIE"),
                  },
                ]}
              >
                {t("Salary.Export")}
              </ActionDropdownButton>
            </ExportRowContent>
          </ListRow>
        ))}
      </ExportGrid>

      {showInfo && (
        <InfoModal
          isOpen={showInfo}
          title={
            errorMessage !== ""
              ? t("Salary.NotAvailable")
              : t("Salary.Downloaded")
          }
          message={
            errorMessage !== ""
              ? t(`Salary.${errorMessage}`)
              : t("Salary.DownloadSuccess")
          }
          confirmText={t("OrganizationalStructure.Ok")}
          onConfirm={() => setShowInfo(false)}
          onCancel={() => setShowInfo(false)}
        />
      )}
    </ExportsPageContainer>
  );
};
